import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  FieldError,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import _get from 'lodash/get';
import { Draggable, DraggableProvided } from '@hello-pangea/dnd';
import {
  FieldComponents,
  FieldItem,
  Value,
  Values,
} from '@lib/interfaces/form';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Tooltip, { TooltipProps } from '@lib/components/Tooltip/Tooltip';
import Typography from '@lib/components/Typography/Typography';
import StrictModeDroppable from '@lib/components/StrictModeDroppable/StrictModeDroppable';
import {
  FieldArrayItemRenderAddMore,
  FieldArrayItemRenderEmptyState,
  FieldArrayItemType,
  SourceAppendValues,
} from '@lib/components/ReactHookForm/types';
import ControllerErrorRender from '@lib/components/ReactHookForm/ControllerErrorRender';
import { useFieldArrayContext } from './FormProvider';

import FieldArrayItem from './FieldArrayItem'; // TODO: fix it
import styles from './Form.module.scss';

interface Props {
  emptyStateText?: string;
  emptyStateTitle?: string;
  entity?: string;
  fieldComponents?: FieldComponents;
  formId?: string;
  formItemsClassName?: string;
  hideErrors?: boolean;
  isDraggable?: boolean;
  isSubItemDisabledFn?: (value: Value, index: number) => boolean;
  isSubItemDraggableFn?: (value: Value, index: number) => boolean;
  isSubItemRemovableFn?: (value: Value, index: number) => boolean;
  isVisible?: boolean;
  isVisibleFn?: (values: Values) => boolean;
  parentName: string;
  sourceAppendValues?: SourceAppendValues;
  subFields: FieldItem[];
  subFieldsAddMoreButtonText?: React.ReactNode;
  subFieldsMaxLength?: number;
  subFieldsMaxLengthTooltipProps?: Omit<TooltipProps, 'children'>;
  subFieldsMinLength?: number;
  title?: string;
}

interface LoopComponentProps {
  fieldItem: FieldArrayItemType;
  groupIndex?: number;
  index: number;
}

interface RenderItemProps {
  groupIndex?: number;
  id?: string;
  index: number;
  subProvided?: DraggableProvided;
}

function FieldArray(props: Props) {
  const {
    emptyStateText,
    emptyStateTitle,
    entity,
    fieldComponents,
    formId,
    formItemsClassName,
    hideErrors,
    isDraggable,
    isSubItemDisabledFn,
    isSubItemDraggableFn,
    isSubItemRemovableFn,
    isVisible = true,
    isVisibleFn,
    parentName,
    sourceAppendValues,
    subFields,
    subFieldsAddMoreButtonText,
    subFieldsMinLength,
    subFieldsMaxLength,
    subFieldsMaxLengthTooltipProps,
    title,
  } = props;
  const { t } = useTranslation();
  const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
  const { fields, append, remove, move, insert, update, replace } =
    useFieldArray({ name: parentName });
  const watch = useWatch({ disabled: isVisibleFn === undefined });
  const { formState } = useFormContext();
  const error = _get(formState, ['errors', parentName]) as
    FieldError | undefined;
  const sourceAppendValue = _get(sourceAppendValues, parentName);

  const FieldArrayAddItemComponent = _get(
    fieldComponents,
    'FieldArrayAddItemComponent',
  );
  const FieldArrayEmptyStateComponent = _get(
    fieldComponents,
    'FieldArrayEmptyStateComponent',
  );
  const FieldArrayLoopComponent = _get(
    fieldComponents,
    'FieldArrayLoopComponent',
  );

  const { updateFieldArrayMethods, removeFieldArrayMethods } =
    useFieldArrayContext();

  // Read through refs so the function registered in the context below
  // always sees the current order, even after a same-length reorder.
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const isSubItemDraggableFnRef = useRef(isSubItemDraggableFn);
  isSubItemDraggableFnRef.current = isSubItemDraggableFn;

  // Get indices of items that cannot be dragged (protected positions)
  const getProtectedIndices = useCallback(() => {
    const isDraggableFn = isSubItemDraggableFnRef.current;
    if (!isDraggableFn) return [];
    return fieldsRef.current.reduce<number[]>((acc, fieldItem, index) => {
      if (!isDraggableFn(fieldItem as Value, index)) {
        acc.push(index);
      }
      return acc;
    }, []);
  }, []);

  const renderItem = ({
    index,
    groupIndex,
    subProvided,
    id,
  }: RenderItemProps) => {
    const onFocus = (e?: React.MouseEvent<HTMLElement>) => {
      e?.stopPropagation();
      setFocusedItemIndex(index);
    };
    const onNextFocus = () => {
      setFocusedItemIndex(index + 1);
    };
    const onPrevFocus = () => {
      setFocusedItemIndex(index > 0 ? index - 1 : 0);
    };
    const isItemFocused = focusedItemIndex === index;
    const fieldItem = fields[index];
    const isSubItemDisabled = isSubItemDisabledFn
      ? isSubItemDisabledFn(fieldItem as Value, index)
      : false;
    const isSubItemDraggable = isSubItemDraggableFn
      ? isSubItemDraggableFn(fieldItem as Value, index)
      : true;
    const isSubItemRemovable = isSubItemRemovableFn
      ? isSubItemRemovableFn(fieldItem as Value, index)
      : true;
    return (
      <FieldArrayItem
        append={append}
        dragHandleProps={subProvided?.dragHandleProps}
        fieldComponents={fieldComponents}
        formId={formId}
        formItemsClassName={formItemsClassName}
        groupIndex={groupIndex}
        id={id}
        index={index}
        isDraggable={isDraggable}
        isFocused={isItemFocused}
        isSubItemDisabled={isSubItemDisabled}
        isSubItemDraggable={isSubItemDraggable}
        isSubItemRemovable={isSubItemRemovable}
        onClick={onFocus}
        onNextFocus={onNextFocus}
        onPrevFocus={onPrevFocus}
        parentName={parentName}
        remove={remove}
        sourceAppendValues={sourceAppendValues}
        subFields={subFields}
        subFieldsMaxLength={subFieldsMaxLength}
        subFieldsMinLength={subFieldsMinLength}
        t={t}
        title={title}
      />
    );
  };

  const renderAddMore: FieldArrayItemRenderAddMore = (
    appendValue,
    appendCallback,
  ) => {
    const resolvedAppendValue = appendValue ?? {};
    let isAddMoreVisible = fields.length > 0;
    if (subFieldsMaxLength)
      isAddMoreVisible = fields.length < subFieldsMaxLength;
    if (FieldArrayAddItemComponent === null) return null;
    if (FieldArrayAddItemComponent) {
      return (
        <FieldArrayAddItemComponent
          append={append}
          appendCallback={appendCallback}
          appendValue={appendValue ?? resolvedAppendValue}
          buttonText={subFieldsAddMoreButtonText}
          isAddMoreVisible={isAddMoreVisible}
          parentName={parentName}
          subFieldsMaxLengthTooltipProps={subFieldsMaxLengthTooltipProps}
        />
      );
    }
    if (!isAddMoreVisible && !subFieldsMaxLengthTooltipProps) return null;
    const addMoreButton = (
      <Button
        buttonText={subFieldsAddMoreButtonText || t(`${entity}-add-more`)}
        buttonType={ButtonTypes.primaryOutlined}
        data-test-id={`field-array__add-button-${parentName}`}
        disabled={!isAddMoreVisible}
        fullWidth
        leftIcon="add"
        onClick={() => {
          append(resolvedAppendValue);
          if (appendCallback) appendCallback();
        }}
      />
    );
    return (
      <div className={styles.subFieldAddMoreBtn}>
        {!isAddMoreVisible && subFieldsMaxLengthTooltipProps ? (
          <Tooltip {...subFieldsMaxLengthTooltipProps} placement="right">
            {addMoreButton}
          </Tooltip>
        ) : (
          addMoreButton
        )}
      </div>
    );
  };

  const renderEmptyState: FieldArrayItemRenderEmptyState = (
    appendValue,
    snapshot,
    visible,
  ) => {
    const resolvedAppendValue = appendValue ?? {};
    const isEmptyStateVisible =
      emptyStateTitle || emptyStateText || fields.length === 0 || visible;
    if (!isEmptyStateVisible) return null;
    if (FieldArrayEmptyStateComponent === null) return null;
    if (FieldArrayEmptyStateComponent) {
      if (fields.length > 0) return null;
      return (
        <FieldArrayEmptyStateComponent
          append={append}
          appendValue={resolvedAppendValue}
          onClick={() => append(resolvedAppendValue)}
          parentName={parentName}
          isUsingPlaceholder={snapshot?.isUsingPlaceholder}
          isAddMoreVisible
        />
      );
    }
    return (
      <div
        className={styles.formItemEmptyState}
        data-test-id={`field-array__empty-state-${parentName}`}
      >
        {emptyStateTitle && (
          <Typography variant="h3" className={styles.subFieldEmptyStateTitle}>
            {emptyStateTitle}
          </Typography>
        )}
        {emptyStateText && (
          <Typography variant="body" className={styles.subFieldEmptyStateText}>
            {emptyStateText}
          </Typography>
        )}
        {fields.length === 0 && (
          <Button
            buttonText={t(`${entity}-add`)}
            buttonType={ButtonTypes.primaryOutlined}
            data-test-id={`field-array__add-button-${parentName}`}
            leftIcon="add"
            onClick={() => append(resolvedAppendValue)}
            fullWidth
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    updateFieldArrayMethods(parentName, {
      append,
      getProtectedIndices,
      insert,
      isSubItemDraggableFn,
      isSubItemRemovableFn,
      move,
      remove,
      replace,
      update,
    });
    return () => {
      removeFieldArrayMethods(parentName);
    };
  }, [
    append,
    getProtectedIndices,
    insert,
    isSubItemDraggableFn,
    isSubItemRemovableFn,
    move,
    parentName,
    remove,
    removeFieldArrayMethods,
    replace,
    update,
    updateFieldArrayMethods,
  ]);

  if (isVisibleFn && !isVisibleFn(watch)) return null;

  if (FieldArrayLoopComponent) {
    return (
      <div
        className={classNames(styles.formItemColumn, {
          [styles.formItemWithErrors]: !hideErrors,
        })}
        data-test-id={`field-array__${parentName}`}
        style={isVisible ? undefined : { display: 'none' }}
      >
        <FieldArrayLoopComponent
          appendValue={sourceAppendValue}
          fields={fields}
          parentName={parentName}
          renderAddMore={renderAddMore}
          renderEmptyState={renderEmptyState}
        >
          {({ fieldItem, index, groupIndex }: LoopComponentProps) => (
            <div
              className={styles.formItemFieldArray}
              key={`${parentName}-${fieldItem.id}`}
            >
              {renderItem({ index, groupIndex, id: fieldItem.id })}
            </div>
          )}
        </FieldArrayLoopComponent>
      </div>
    );
  }

  if (!isDraggable) {
    return (
      <div
        className={classNames(styles.formItemColumn, {
          [styles.formItemWithErrors]: !hideErrors,
        })}
        data-test-id={`field-array__${parentName}`}
        style={isVisible ? undefined : { display: 'none' }}
      >
        {renderEmptyState(sourceAppendValue)}
        {fields.map((fieldItem, index) => (
          <div
            className={styles.formItemFieldArray}
            key={`${parentName}-${fieldItem.id}`}
          >
            {renderItem({ index, id: fieldItem.id })}
          </div>
        ))}
        {!hideErrors && !!error && !Array.isArray(error) && (
          <div className={styles.formIFieldArrayError}>
            <ControllerErrorRender
              error={error}
              data-test-id={`field-array__error-${parentName}`}
            />
          </div>
        )}
        {renderAddMore(sourceAppendValue)}
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.formItemColumn, {
        [styles.formItemWithErrors]: !hideErrors,
      })}
      data-test-id={`field-array__${parentName}`}
      style={isVisible ? undefined : { display: 'none' }}
    >
      <StrictModeDroppable droppableId={`FieldArrayDroppable-${parentName}`}>
        {(provided, snapshot) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {renderEmptyState(sourceAppendValue, snapshot)}
            {fields.map((fieldItem, index) => (
              <Draggable
                key={`${parentName}-${fieldItem.id}`}
                draggableId={`${parentName}-${fieldItem.id}`}
                index={index}
                isDragDisabled={
                  isSubItemDraggableFn
                    ? !isSubItemDraggableFn(fieldItem as Value, index)
                    : false
                }
              >
                {(subProvided, subSnapshot) => (
                  <div
                    className={classNames(styles.formItemFieldArray, {
                      [styles.formItemFieldArrayDragging]:
                        subSnapshot.isDragging,
                    })}
                    ref={subProvided.innerRef}
                    {...subProvided.draggableProps}
                    style={subProvided.draggableProps.style}
                  >
                    {renderItem({ index, subProvided, id: fieldItem.id })}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
      {!hideErrors && !!error && !Array.isArray(error) && (
        <div className={styles.formIFieldArrayError}>
          <ControllerErrorRender
            error={error}
            data-test-id={`field-array__error-${parentName}`}
          />
        </div>
      )}
      {renderAddMore(sourceAppendValue)}
    </div>
  );
}

export default memo(FieldArray);
